import {
  Alert,
  Button,
  CountryCode,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  FlagComponent,
  Icons,
  Label,
  Textarea,
} from '@commenda/kakashi';
import { ServiceRequestDetails } from '@commenda/types/src/service-request/types';
import { ServiceRequestModel } from '@commenda/utils/models/service-request';
import { zodResolver } from '@hookform/resolvers/zod';
import { Country as CountryPkg } from 'country-state-city';
import * as z from 'zod';
import { useForm } from '../../../hooks/useZodForm';
import { getServiceRequestServiceDetails } from '../../../service-request/constants';

export const SkipAndReopenTaskConfirmationSchema = z.object({
  reason: z.string().trim().min(1, { message: 'Please enter valid reason' }),
});

export type SkipAndReopenTaskConfirmationSchemaFields = z.infer<
  typeof SkipAndReopenTaskConfirmationSchema
>;

interface SkipAndReopenTaskConfirmationModalProps {
  isOpen: boolean;
  closeModal: () => void;
  onContinue: (reason: string) => void;
  serviceRequest: ServiceRequestDetails;
  buttonConfirmationText: string;
  currentTaskTitle: string;
}

const SkipAndReopenTaskConfirmationModal = ({
  isOpen,
  closeModal,
  onContinue,
  serviceRequest,
  buttonConfirmationText,
  currentTaskTitle,
}: SkipAndReopenTaskConfirmationModalProps) => {
  const {
    handleSubmit,
    register,
    setValue,
    formState: { errors, isSubmitting },
    watch,
  } = useForm<SkipAndReopenTaskConfirmationSchemaFields>({
    resolver: zodResolver(SkipAndReopenTaskConfirmationSchema),
    defaultValues: { reason: '' },
  });

  const reason = watch('reason');
  const isButtonDisabled = isSubmitting || !reason;

  const incorporationCountry =
    getServiceRequestServiceDetails(serviceRequest).country ??
    serviceRequest?.corporation?.incorporationCountry;

  const countryName = CountryPkg.getCountryByCode(incorporationCountry)?.name;

  const companyName =
    ServiceRequestModel.getServiceRequestCompanyName(serviceRequest);

  const serviceDisplayName =
    getServiceRequestServiceDetails(serviceRequest).displayName;

  const title = buttonConfirmationText;
  const subheading =
    buttonConfirmationText === 'Skip task'
      ? 'You are skipping'
      : 'You are reopening';

  if (!serviceRequest) {
    return null;
  }

  return (
    <Dialog open={isOpen} onOpenChange={closeModal}>
      <DialogContent
        className="flex flex-col max-w-2xl"
        hideCloseIcon
        aria-describedby={undefined}
      >
        <DialogHeader className="flex flex-col items-start border-b-0">
          <DialogTitle>
            <div className="flex items-center gap-2">
              <p>{title}</p>
              <span className="text-text-tertiary">•</span>
              <div className="size-5">
                <FlagComponent
                  country={incorporationCountry as CountryCode}
                  countryName={countryName}
                />
              </div>
              <span className="txt-small-medium text-text-tertiary">
                {companyName || 'Not Decided'}
              </span>
            </div>
          </DialogTitle>
          <p className="txt-small-regular text-text-tertiary">
            {subheading} the task{' '}
            <span className="font-bold">{currentTaskTitle}</span> for the
            service <span className="font-bold">{serviceDisplayName}</span>
          </p>
        </DialogHeader>

        <div className="px-6">
          <Alert
            title="This reason will be recorded for future reference. This action cannot be reversed."
            variant="warning"
            icon={<Icons.AlertTriangle className="size-5 text-fg-warning" />}
            classNames={{ title: 'txt-small-regular !font-normal' }}
          />
          <div className="flex flex-col gap-2 mb-5">
            <Label className="text-text-secondary-700">Reason</Label>
            <Textarea
              {...register('reason')}
              value={reason}
              error={errors.reason?.message}
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant="secondary" onClick={closeModal}>
            Cancel
          </Button>
          <Button
            variant={isButtonDisabled ? 'primary' : 'destructive-primary'}
            onClick={handleSubmit((data) => {
              onContinue(data.reason);
              setValue('reason', '');
            })}
            disabled={isButtonDisabled}
          >
            {buttonConfirmationText}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export { SkipAndReopenTaskConfirmationModal };
